import { Spacing } from '@persona-id/design-tokens';
import { DateTime } from 'luxon';
import { Link } from 'react-router-dom-v5-compat';
import styled from 'styled-components';

import ContextMenu from 'dashboard/components/ContextMenu';
import HoverCopyableField from 'dashboard/components/HoverCopyableField';
import LoadingIndicator from 'dashboard/components-lib/LoadingIndicator';
import { TableNullState } from 'dashboard/layouts/TableLayout';
import { SortParams } from 'dashboard/lib/interfaces/SortParams';
import {
  GraphQueryTemplate,
  GraphQueryTemplateId,
  GraphQueryTemplateStatus,
} from 'dashboard/models/graph-query-template';
import Table, {
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
} from 'design-system/components/Table';
import { DATE_AND_TIME_TZ_FORMAT } from 'lib/constants/luxonFormats';

interface GraphQueryTemplatesTableProps {
  archiveGraphQueryTemplate: (id: GraphQueryTemplateId) => void;
  graphQueryTemplates: GraphQueryTemplate[];
  handleSortUpdate: (sortParams: SortParams) => void;
  isLoadingGraphQueryTemplates: boolean;
  restoreGraphQueryTemplate: (id: GraphQueryTemplateId) => void;
  sortParams: SortParams;
}

const TableActionCell = styled(TableCell)`
  text-align: right;
  padding: 0;
  padding-right: ${Spacing[4]};
  width: 48px;
`;

const GraphQueryTemplatesTable = ({
  archiveGraphQueryTemplate,
  graphQueryTemplates,
  handleSortUpdate,
  isLoadingGraphQueryTemplates,
  restoreGraphQueryTemplate,
  sortParams,
}: GraphQueryTemplatesTableProps) => {
  const handleColumnHeaderClick = (columnName: string) => {
    handleSortUpdate({
      sort: columnName,
      shouldSortDescending:
        columnName === sortParams.sort ? !sortParams.shouldSortDescending : true,
    });
  };
  const getSortDirection = (columnName: string) => {
    if (columnName === sortParams.sort) {
      return sortParams.shouldSortDescending ? 'desc' : 'asc';
    }
    return false;
  };

  if (isLoadingGraphQueryTemplates) {
    return <LoadingIndicator />;
  } else if (graphQueryTemplates.length === 0) {
    return (
      <TableNullState dataTestBlock='graph-query-templates-table__null-state'>
        <h3>Looks like there aren&#39;t any query templates yet.</h3>
        <p>Create a query template to get started.</p>
      </TableNullState>
    );
  } else {
    return (
      <Table>
        <TableHead>
          <TableRow>
            <TableHeaderCell
              data-test='graph-query-templates-table__name-header'
              id='graph-query-templates-table__name-header'
              sortable={true}
              onClick={() => handleColumnHeaderClick('name')}
              sortDirection={getSortDirection('name')}
            >
              Name
            </TableHeaderCell>
            <TableHeaderCell
              data-test='graph-query-templates-table__token-header'
              id='graph-query-templates-table__token-header'
            >
              ID
            </TableHeaderCell>
            <TableHeaderCell
              data-test='graph-query-templates-table__updated-at-header'
              id='graph-query-templates-table__updated-at-header'
              sortable={true}
              onClick={() => handleColumnHeaderClick('updated-at')}
              sortDirection={getSortDirection('updated-at')}
            >
              Last updated at
            </TableHeaderCell>
            <TableHeaderCell>{/* Actions column */}</TableHeaderCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {graphQueryTemplates.map((graphQueryTemplate) => (
            <TableRow
              as={Link}
              to={`/dashboard/explorer?queryTemplateId=${graphQueryTemplate.id}`}
              key={graphQueryTemplate.id}
            >
              <TableCell>{graphQueryTemplate.attributes.name}</TableCell>
              <TableCell>
                <HoverCopyableField
                  copyValue={graphQueryTemplate.id}
                  confirmationText='Copied Query Template ID'
                >
                  {graphQueryTemplate.id}
                </HoverCopyableField>
              </TableCell>
              <TableCell>
                {DateTime.fromISO(graphQueryTemplate.attributes['updated-at']).toFormat(
                  DATE_AND_TIME_TZ_FORMAT,
                )}
              </TableCell>
              <TableActionCell>
                <ContextMenu
                  items={[
                    {
                      id: 'archive',
                      onClick: () => {
                        archiveGraphQueryTemplate(graphQueryTemplate.id);
                      },
                      hidden:
                        graphQueryTemplate.attributes.status === GraphQueryTemplateStatus.Archived,
                      variant: 'danger',
                      label: 'Archive',
                    },
                    {
                      id: 'restore',
                      onClick: () => {
                        restoreGraphQueryTemplate(graphQueryTemplate.id);
                      },
                      hidden:
                        graphQueryTemplate.attributes.status === GraphQueryTemplateStatus.Active,
                      variant: 'danger',
                      label: 'Restore',
                    },
                  ]}
                  renderItem={(item) => (
                    <ContextMenu.Item
                      id={`graph-query-templates-table__row--${graphQueryTemplate.id}__${item.id}`}
                      key={`graph-query-templates-table__row--${graphQueryTemplate.id}__${item.id}`}
                      onClick={(e) => {
                        // Needed to avoid triggering row click below
                        e.stopPropagation();
                        e.preventDefault();
                        item.onClick && item.onClick();
                      }}
                      variant={item.variant}
                    >
                      {item.label}
                    </ContextMenu.Item>
                  )}
                />
              </TableActionCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    );
  }
};

export default GraphQueryTemplatesTable;
